import { Navigate, createBrowserRouter, useSearchParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import { PreviousLocationProvider } from '../../providers/PreviousLocationProvider';
import DesignRoute from '../../components/DesignRoute';
import RouteLoading from '../../components/RouteLoading';
import ErrorBoundary from '../../components/ErrorBoundary';
import { useUserCompaniesCurrentUserAdminState } from '../../services/user.service';
import { USER_ROLES } from '../../consts/userRoles';
import { NoAccess } from '../../components/NoAccess';
import RequireFeatureFlag from '../../components/RequireFeatureFlag';
import { FEATURE_FLAG_NAMES } from '../../hooks/useFeatureFlag';
import { lazyWithRetry } from '../../utils/lazyWithRetry';
import WorkflowRouteGuard from '../../components/WorkflowRouteGuard';
import { REPORT_TYPES, periodReportTypes } from '../../consts/reportTypes';

const OutputTables = lazyWithRetry(() => import('../../pages/OutputTables'));
const Output = lazyWithRetry(() => import('../../pages/Output'));
const Reportings = lazyWithRetry(() => import('../../pages/Dashboard/Reportings'));
const SelectFiles = lazyWithRetry(() => import('../../pages/Dashboard/Reportings/SelectFiles'));
const FCAReport = lazyWithRetry(() => import('../../pages/Report/FCA'));
const CreateProject = lazyWithRetry(() => import('../../pages/Project/Create'));
const Project = lazyWithRetry(() => import('../../pages/Project'));
const Template = lazyWithRetry(() => import('../../pages/Project/Template'));
const Periods = lazyWithRetry(() => import('../../pages/Project/Periods'));
const DataExtraction = lazyWithRetry(() => import('../../pages/DataExtraction'));
const SourceFilesRoute = lazyWithRetry(() => import('../../pages/SourceFilesV2/SourceFilesRoute'));
const ESMAReport = lazyWithRetry(() => import('../../pages/Report/ESMA'));
const AddAIFs = lazyWithRetry(() => import('../../pages/Project/Quarters/AddAIFs'));
const AddFile = lazyWithRetry(() => import('../../pages/Project/Quarters/AddFile'));
const Jurisdictions = lazyWithRetry(() => import('../../pages/Project/Quarters/Jurisdictions'));
const JurisdictionsV2 = lazyWithRetry(() => import('../../pages/Project/Quarters/JurisdictionsV2'));
const AddAIFsV2 = lazyWithRetry(() => import('../../pages/Project/Quarters/AddAIFsV2'));
const ManageUsers = lazyWithRetry(() => import('../../pages/ManageUsers/index'));
const ActivityLog = lazyWithRetry(() => import('../../pages/ActivityLog'));
const FormPF = lazyWithRetry(() => import('../../pages/Report/FormPF'));
const IntegrationsPage = lazyWithRetry(() => import('../../pages/Integrations'));
const Setup = lazyWithRetry(() => import('../../pages/Setup'));
const ExtractionV2 = lazyWithRetry(() => import('../../pages/ExtractionV2'));
const TransformationV2 = lazyWithRetry(() => import('../../pages/TransformationV2'));
const ReportV2 = lazyWithRetry(() => import('../../pages/ReportV2'));
const ReportV2Page = lazyWithRetry(() =>
  import('../../pages/ReportV2').then(module => ({ default: module.ReportV2Page }))
);
const ProjectFunds = lazyWithRetry(() => import('../../pages/Project/Funds'));
const PriipsDemo = lazyWithRetry(() => import('../../pages/PriipsDemo'));

const withPreviousLocation = element => (
  <PreviousLocationProvider>{element}</PreviousLocationProvider>
);

const AdminRoute = ({ children }) => {
  const { data: currentUserState, isLoading } = useUserCompaniesCurrentUserAdminState();

  if (isLoading) {
    return <RouteLoading />;
  }

  if (currentUserState?.role !== USER_ROLES.ADMIN && !currentUserState?.is_super_admin) {
    return <Navigate to="/project" replace />;
  }

  return children;
};

/**
 * The extraction address, in whichever design the reader works in.
 *
 * The classic page is one page with three tabs; the redesign gave two of those tabs a page each.
 * So the tab in the query is what picks, and the third — lookup tables, which has no redesigned
 * page — stays on the classic one for everybody.
 */
const ExtractionTabRoute = () => {
  const [params] = useSearchParams();
  const tab = params.get('tab') || 'extraction';

  if (tab === 'transformation')
    return <DesignRoute v2={<TransformationV2 />} classic={<DataExtraction />} />;
  if (tab === 'extraction')
    return <DesignRoute v2={<ExtractionV2 />} classic={<DataExtraction />} />;

  return <DataExtraction />;
};

export const privateRoutes = createBrowserRouter([
  {
    path: '/activity-log',
    element: withPreviousLocation(<Layout />),
    children: [
      {
        index: true,
        element: (
          <ErrorBoundary componentName="ActivityLog">
            <ActivityLog />
          </ErrorBoundary>
        )
      }
    ]
  },
  {
    path: '/data-extraction/:id/:periodId',
    element: withPreviousLocation(<Layout />),
    children: [
      {
        index: true,
        element: (
          <WorkflowRouteGuard allowedReportTypes={periodReportTypes}>
            <ErrorBoundary componentName="DataExtraction">
              {/* Three tabs on the classic page, and two of them have a redesigned page of their
                  own. Lookup tables has none, so it stays classic in both designs — the stepper
                  says as much. */}
              <ExtractionTabRoute />
            </ErrorBoundary>
          </WorkflowRouteGuard>
        )
      }
    ]
  },
  {
    path: '/setup/:id/:periodId',
    element: withPreviousLocation(<Layout />),
    children: [
      {
        index: true,
        element: (
          <RequireFeatureFlag flag={FEATURE_FLAG_NAMES.V2_UI} fallback="/project">
            <ErrorBoundary componentName="Setup">
              <WorkflowRouteGuard allowedReportTypes={periodReportTypes}>
                <Setup />
              </WorkflowRouteGuard>
            </ErrorBoundary>
          </RequireFeatureFlag>
        )
      }
    ]
  },
  {
    // Redesigned Jurisdictions (opt-in, legacy untouched — the old route and its page stay exactly
    // as they were). Same container, same hook, same ESMAJurisdiction table; only the layout differs,
    // so the two can be compared side by side while this is behind the flag.
    path: '/jurisdictions-v2/:id/:periodId',
    element: withPreviousLocation(<Layout />),
    children: [
      {
        index: true,
        element: (
          <RequireFeatureFlag flag={FEATURE_FLAG_NAMES.V2_UI} fallback="/project">
            <ErrorBoundary componentName="JurisdictionsV2">
              <WorkflowRouteGuard allowedReportTypes={periodReportTypes}>
                <JurisdictionsV2 />
              </WorkflowRouteGuard>
            </ErrorBoundary>
          </RequireFeatureFlag>
        )
      }
    ]
  },
  {
    // Redesigned Jurisdictions (opt-in, legacy untouched — the old route and its page stay exactly
    // as they were). Same container, same hook, same ESMAJurisdiction table; only the layout differs,
    // so the two can be compared side by side while this is behind the flag.
    path: '/aifs-v2/:id/:periodId',
    element: withPreviousLocation(<Layout />),
    children: [
      {
        index: true,
        element: (
          <RequireFeatureFlag flag={FEATURE_FLAG_NAMES.V2_UI} fallback="/project">
            <ErrorBoundary componentName="AddAIFsV2">
              <WorkflowRouteGuard allowedReportTypes={periodReportTypes}>
                <AddAIFsV2 />
              </WorkflowRouteGuard>
            </ErrorBoundary>
          </RequireFeatureFlag>
        )
      }
    ]
  },
  {
    // New redesigned extraction page (opt-in, legacy untouched). Mounted INSIDE the
    // app Layout — same as the legacy extraction page — so the app's common chrome
    // (AppTopBar + WorkflowStepper + WorkflowToolbar) is the navigation/steps/action-bar.
    // Use the :id/:periodId path so that chrome populates from the workflow context;
    // the bare path renders the workspace with empty chrome.
    path: '/extraction-v2',
    element: withPreviousLocation(<Layout />),
    children: [
      {
        index: true,
        element: (
          <RequireFeatureFlag flag={FEATURE_FLAG_NAMES.V2_UI} fallback="/project">
            <ErrorBoundary componentName="ExtractionV2">
              <ExtractionV2 />
            </ErrorBoundary>
          </RequireFeatureFlag>
        )
      }
    ]
  },
  {
    path: '/extraction-v2/:id/:periodId',
    element: withPreviousLocation(<Layout />),
    children: [
      {
        index: true,
        element: (
          <RequireFeatureFlag flag={FEATURE_FLAG_NAMES.V2_UI} fallback="/project">
            <ErrorBoundary componentName="ExtractionV2">
              <WorkflowRouteGuard allowedReportTypes={periodReportTypes}>
                <ExtractionV2 />
              </WorkflowRouteGuard>
            </ErrorBoundary>
          </RequireFeatureFlag>
        )
      }
    ]
  },
  {
    // New redesigned transformation page (opt-in, legacy untouched). Wired to api/transform; the
    // workflow/period in the path scope every query, so there is no bare variant of this route.
    path: '/transformation-v2/:id/:periodId',
    element: withPreviousLocation(<Layout />),
    children: [
      {
        index: true,
        element: (
          <RequireFeatureFlag flag={FEATURE_FLAG_NAMES.V2_UI} fallback="/project">
            <ErrorBoundary componentName="TransformationV2">
              <WorkflowRouteGuard allowedReportTypes={periodReportTypes}>
                <TransformationV2 />
              </WorkflowRouteGuard>
            </ErrorBoundary>
          </RequireFeatureFlag>
        )
      }
    ]
  },
  {
    // New redesigned Output page (opt-in, legacy untouched). Sibling of transformation-v2 but wired
    // to the output-transform layer; the workflow/period in the path scope every query.
    path: '/output/:id/:periodId',
    element: withPreviousLocation(<Layout />),
    children: [
      {
        index: true,
        element: (
          <RequireFeatureFlag flag={FEATURE_FLAG_NAMES.V2_UI} fallback="/project">
            <ErrorBoundary componentName="Output">
              <WorkflowRouteGuard allowedReportTypes={periodReportTypes}>
                <Output />
              </WorkflowRouteGuard>
            </ErrorBoundary>
          </RequireFeatureFlag>
        )
      }
    ]
  },
  {
    // New redesigned report page (opt-in, legacy untouched). Same Layout-mounted pattern as
    // extraction-v2 so the app chrome drives navigation; the :id/:periodId path populates it.
    path: '/report-v2',
    element: withPreviousLocation(<Layout />),
    children: [
      {
        index: true,
        element: (
          <RequireFeatureFlag flag={FEATURE_FLAG_NAMES.V2_UI} fallback="/project">
            <ErrorBoundary componentName="ReportV2">
              <WorkflowRouteGuard allowedReportTypes={periodReportTypes}>
                <ReportV2 />
              </WorkflowRouteGuard>
            </ErrorBoundary>
          </RequireFeatureFlag>
        )
      }
    ]
  },
  {
    path: '/report-v2/:id/:periodId',
    element: withPreviousLocation(<Layout />),
    children: [
      {
        index: true,
        element: (
          <RequireFeatureFlag flag={FEATURE_FLAG_NAMES.V2_UI} fallback="/project">
            <WorkflowRouteGuard
              allowedReportTypes={[
                REPORT_TYPES.ANNEX_IV,
                REPORT_TYPES.FORM_PF,
                REPORT_TYPES.CUSTOM,
                REPORT_TYPES.CONVERSION
              ]}
            >
              <ErrorBoundary componentName="ReportV2">
                <ReportV2Page />
              </ErrorBoundary>
            </WorkflowRouteGuard>
          </RequireFeatureFlag>
        )
      }
    ]
  },
  {
    path: '/',
    element: withPreviousLocation(<Layout />),
    children: [
      {
        path: 'project',
        children: [
          {
            index: true,
            element: (
              <ErrorBoundary componentName="Project">
                <Project />
              </ErrorBoundary>
            )
          },
          {
            path: 'quaters/:id',
            children: [
              {
                index: true,
                element: (
                  <WorkflowRouteGuard allowedReportTypes={periodReportTypes}>
                    <Periods />
                  </WorkflowRouteGuard>
                )
              },
              {
                path: ':periodId/add-aifs',
                children: [
                  {
                    index: true,
                    element: (
                      <WorkflowRouteGuard allowedReportTypes={periodReportTypes}>
                        <DesignRoute v2={<AddAIFsV2 />} classic={<AddAIFs />} />
                      </WorkflowRouteGuard>
                    )
                  }
                ]
              },
              {
                path: ':periodId/add-file',
                children: [
                  {
                    index: true,
                    element: (
                      <WorkflowRouteGuard allowedReportTypes={periodReportTypes}>
                        <AddFile />
                      </WorkflowRouteGuard>
                    )
                  }
                ]
              },
              {
                path: ':periodId/upload-files',
                children: [
                  {
                    index: true,
                    element: (
                      <WorkflowRouteGuard allowedReportTypes={periodReportTypes}>
                        <SourceFilesRoute />
                      </WorkflowRouteGuard>
                    )
                  }
                ]
              },
              {
                path: ':periodId/jurisdictions',
                element: (
                  <WorkflowRouteGuard allowedReportTypes={periodReportTypes}>
                    <DesignRoute v2={<JurisdictionsV2 />} classic={<Jurisdictions />} />
                  </WorkflowRouteGuard>
                )
              }
            ]
          },
          {
            path: ':projectId',
            children: [
              {
                index: true,
                element: (
                  <WorkflowRouteGuard allowedReportTypes={periodReportTypes}>
                    <Template />
                  </WorkflowRouteGuard>
                )
              },
              {
                path: 'funds',
                element: (
                  <WorkflowRouteGuard allowedReportTypes={periodReportTypes}>
                    <ProjectFunds />
                  </WorkflowRouteGuard>
                )
              },
              {
                path: 'jurisdictions',
                element: (
                  <WorkflowRouteGuard allowedReportTypes={periodReportTypes}>
                    <DesignRoute v2={<JurisdictionsV2 />} classic={<Jurisdictions />} />
                  </WorkflowRouteGuard>
                )
              },
              {
                path: 'create',
                element: (
                  <WorkflowRouteGuard allowedReportTypes={periodReportTypes}>
                    <CreateProject isCreate={true} />
                  </WorkflowRouteGuard>
                )
              },
              {
                path: ':templateId',
                element: (
                  <WorkflowRouteGuard allowedReportTypes={periodReportTypes}>
                    <CreateProject />
                  </WorkflowRouteGuard>
                )
              }
            ]
          }
        ]
      },

      {
        path: 'dashboard',
        element: <Layout />,
        children: [
          {
            index: true,
            // element: <Dashboard />,
            element: <Navigate to="/project" />
          },
          {
            path: 'reportings',
            children: [
              {
                index: true,
                element: (
                  <ErrorBoundary componentName="Reportings">
                    <Reportings />
                  </ErrorBoundary>
                )
              },
              {
                path: 'report',
                children: [
                  {
                    path: ':reportId',
                    element: (
                      <ErrorBoundary componentName="FCAReport">
                        <WorkflowRouteGuard allowedReportTypes={[REPORT_TYPES.ANNEX_IV]}>
                          <FCAReport />
                        </WorkflowRouteGuard>
                      </ErrorBoundary>
                    )
                  }
                ]
              },
              {
                path: ':integration',
                children: [
                  {
                    path: 'files',
                    element: <SelectFiles />
                  }
                ]
              }
            ]
          }
        ]
      },
      {
        path: `report/fca/:reportingMemberState/:workflowId/:periodId`,
        element: (
          <WorkflowRouteGuard
            allowedReportTypes={[
              REPORT_TYPES.ANNEX_IV,
              REPORT_TYPES.CUSTOM,
              REPORT_TYPES.CONVERSION
            ]}
          >
            <ErrorBoundary componentName="FCAReport">
              <FCAReport />
            </ErrorBoundary>
          </WorkflowRouteGuard>
        )
      },
      {
        path: `report/esma/:reportingMemberState/:workflowId/:periodId`,
        element: (
          <WorkflowRouteGuard
            allowedReportTypes={[
              REPORT_TYPES.ANNEX_IV,
              REPORT_TYPES.CUSTOM,
              REPORT_TYPES.CONVERSION
            ]}
          >
            <ErrorBoundary componentName="ESMAReport">
              <ESMAReport />
            </ErrorBoundary>
          </WorkflowRouteGuard>
        )
      },
      {
        path: `report/pf/:reportingMemberState/:workflowId/:periodId`,
        element: (
          <WorkflowRouteGuard allowedReportTypes={[REPORT_TYPES.FORM_PF]}>
            <ErrorBoundary componentName="FormPF">
              <FormPF />
            </ErrorBoundary>
          </WorkflowRouteGuard>
        )
      },
      {
        path: 'integrations',
        element: <IntegrationsPage />
      },
      {
        path: 'output-tables/:projectId/:periodId',
        element: (
          <WorkflowRouteGuard allowedReportTypes={periodReportTypes}>
            <ErrorBoundary componentName="OutputTables">
              <DesignRoute v2={<Output />} classic={<OutputTables />} />
            </ErrorBoundary>
          </WorkflowRouteGuard>
        )
      },
      {
        path: 'templates',
        element: <>Saved templates</>
      },
      {
        path: 'Payment',
        element: <>Payment</>
      },
      {
        path: 'help',
        element: <>Help</>
      },
      {
        path: 'access-denied',
        element: <NoAccess />
      },
      {
        path: 'settings',
        element: (
          <AdminRoute>
            <ManageUsers />
          </AdminRoute>
        )
      },
      {
        path: 'priips-demo',
        element: (
          <ErrorBoundary componentName="PriipsDemo">
            <PriipsDemo />
          </ErrorBoundary>
        )
      },
      {
        path: 'priips-demo/:workflowId',
        element: (
          <ErrorBoundary componentName="PriipsDemo">
            <PriipsDemo />
          </ErrorBoundary>
        )
      },
      {
        path: '*',
        element: <Navigate to="/project" />
      },
      {
        index: true,
        element: <Navigate to="/project" />
      },
      {}
    ]
  },
  {
    path: '*',
    element: <Navigate to="/project" />
  }
]);
